/* eslint-disable react/no-unescaped-entities */
import React from 'react';

import { Button, Typography, Box } from '@mui/material';
import { useAddFavoriteCodesMutation } from 'src/common/apis/FlorenceApi';
import { SelectableCode } from 'src/common/types/Codes';

import { ReactComponent as BookmarkIconSmallWhite } from '../../../common/assets/icons/bookmark_icon_small_white.svg';
import { ReactComponent as CheckmarkIcon } from '../../../common/assets/icons/checkmark_icon_white.svg';
import { ReactComponent as SheetIcon } from '../../../common/assets/icons/sheet_icon.svg';
import SnackbarUtils from '../../../common/utils/SnackbarUtilsConfigurator';
import './BottomResultsBar.scss';

type Props = {
  onSaveSheet: () => void;
  onClickFinalize: () => void;
  selectedCodes: SelectableCode[];
  open: boolean;
  searchType: 'text' | 'scan';
  incorrectCodeCombinations?: boolean;
  disabled?: boolean;
  isClinicNote?: boolean;
};

const BottomResultsBar: React.FC<Props> = ({
  onSaveSheet,
  onClickFinalize,
  selectedCodes,
  open,
  searchType,
  incorrectCodeCombinations = false,
  disabled = false,
  isClinicNote = false,
}) => {
  const [addFavoriteCodes, { isLoading: saveCodeIsLoading }] =
    useAddFavoriteCodesMutation();

  const saveFavoriteCodes = async () => {
    if (selectedCodes.length === 0) return;

    addFavoriteCodes(selectedCodes)
      .then(() => {
        SnackbarUtils.toast(
          selectedCodes.length === 1
            ? 'Favorite Code Saved'
            : 'Favorite Codes Saved'
        );
      })
      .catch((error) => {
        if (error.message) {
          SnackbarUtils.error(error.message);
        }
      });
  };

  return (
    <Box className='bottom-bar-container'>
      <Box className='bottom-bar-main-section'>
        <Typography noWrap color='white'>
          {selectedCodes.length} Code{selectedCodes.length === 1 ? '' : 's'}{' '}
          Selected
        </Typography>

        <Box
          display={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <Button
            disabled={
              disabled || selectedCodes.length === 0 || saveCodeIsLoading
            }
            sx={{
              borderRadius: '100px',
            }}
            onClick={() => saveFavoriteCodes()}
            color='defaultWhite'
            variant='outlined'
            startIcon={<BookmarkIconSmallWhite />}
          >
            Save Favorite Code{selectedCodes.length === 1 ? '' : 's'}
          </Button>
          {searchType === 'scan' && (
            <>
              <Button
                disabled={disabled}
                sx={{
                  borderRadius: '100px',
                }}
                onClick={() => onSaveSheet()}
                color='defaultWhite'
                variant='outlined'
                startIcon={<SheetIcon style={{ fill: 'white' }} />}
              >
                {isClinicNote ? 'Save Clinic Note' : 'Save Op Note'}
              </Button>
              {!isClinicNote && (
                <Button
                  disabled={disabled || selectedCodes.length === 0}
                  sx={{
                    borderRadius: '100px',
                  }}
                  onClick={() => onClickFinalize()}
                  color='defaultWhite'
                  variant='outlined'
                  startIcon={<CheckmarkIcon />}
                >
                  Finalize
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      {incorrectCodeCombinations && (
        <Box className='incorrect-cci-container'>
          <Typography color='white' className='incorrect-cci-title'>
            Incorrect CCI code combination!
          </Typography>
          <Typography fontSize='12px' color='white' className='hide-on-mobile'>
            Hover over a code's error icon to view conflicts
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BottomResultsBar;
